<template>
  <DataTable
    v-model:multi-sort-meta="multiSortMeta"
    data-cy="perfevals-data-table"
    sort-mode="multiple"
    :first="offset"
    :value="perfevals"
    :total-records="perfevalsAggregateCount"
    :rows="limit"
    :selection-mode="checkedRows ? 'multiple' : undefined"
    :selection="checkedRows"
    auto-layout
    lazy
    paginator
    paginator-position="bottom"
    paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
    responsive-layout="stack"
    current-page-report-template="Showing {first} to {last} of {totalRecords}"
    @update:first="$emit('update:offset', $event)"
    @update:limit="$emit('update:limit', $event)"
    @update:selection="$emit('update:checked-rows', $event)"
  >
    <template #empty>
      <p class="text-center" data-cy="no-evaluations-found">
        No evaluations found.
      </p>
    </template>
    <Column
      v-if="!!checkedRows"
      selectionMode="multiple"
      headerStyle="width: 3rem"
    ></Column>
    <column field="title" header="Name" :sortable="true">
      <template #body="{ data }">
        <router-link
          v-if="renderLinks"
          :to="{
            name: 'PerformanceEvaluation',
            params: {
              sizingId: (data.scenario || data.r_scenario).sizing_id,
              scenarioId: (data.scenario || data.r_scenario).id,
              perfevalId: data.id
            }
          }"
          data-cy="perfeval-name-link"
        >
          {{ data.title || "Untitled" }}
        </router-link>
        <span v-else>
          {{ data.title || "Untitled" }}
        </span>
      </template>
    </column>
    <column
      v-if="!hideWell"
      field="scenario.sizing.well.well_name"
      header="Well"
      :sortable="true"
    >
      <template #body="{ data }">
        <router-link
          v-if="
            (data.scenario || data.r_scenario).sizing.well?.id && renderLinks
          "
          :to="{
            name: 'Well',
            params: { well: (data.scenario || data.r_scenario).sizing.well?.id }
          }"
        >
          {{ (data.scenario || data.r_scenario)?.sizing.well?.well_name }}
        </router-link>
        <span v-else-if="(data.scenario || data.r_scenario).sizing.well?.id">
          {{ (data.scenario || data.r_scenario)?.sizing.well?.well_name }}
        </span>
      </template>
    </column>
    <column
      field="scenario.sizing.customer.customer_name"
      header="Customer"
      :sortable="true"
    />
    <column
      v-if="!hideOwner"
      field="author.display_name"
      header="Owner"
      :sortable="false"
    />
    <column field="scenario.title" header="Scenario">
      <template #body="{ data }">
        <router-link
          :to="{
            name: 'WellInfo',
            params: {
              scenarioId: (data.scenario || data.r_scenario)?.id,
              sizingId: (data.scenario || data.r_scenario).sizing_id
            }
          }"
        >
          {{ (data.scenario || data.r_scenario)?.title }}
        </router-link>
      </template>
    </column>
    <column field="scenario.sizing.title" header="Sizing">
      <template #body="{ data }">
        <router-link
          :to="{
            name: 'WellInfo',
            params: {
              scenarioId: (data.scenario || data.r_scenario)?.id,
              sizingId: (data.scenario || data.r_scenario).sizing_id
            }
          }"
        >
          {{ (data.scenario || data.r_scenario)?.sizing.title }}
        </router-link>
      </template>
    </column>
    <column field="journal_updated_at" header="Last Updated" :sortable="true">
      <template #body="{ data }">
        {{ new Date(data.journal_updated_at).toLocaleDateString() }}
      </template>
    </column>
  </DataTable>
</template>

<script setup lang="ts">
import type {
  PerformanceEvaluationsQuery,
  Performance_Evaluations_Order_By
} from "gql/api";

import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { usePrimeVueMultisort } from "@/composables/primevue/tables";
import { watch } from "vue";

const props = defineProps<{
  perfevals: PerformanceEvaluationsQuery["performance_evaluations"];
  perfevalsAggregateCount: number;
  limit: number;
  offset: number;
  orderBy: Performance_Evaluations_Order_By[];
  hideOwner?: boolean;
  hideWell?: boolean;
  checkedRows?: PerformanceEvaluationsQuery["performance_evaluations"];
  renderLinks?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:order-by", value: Performance_Evaluations_Order_By[]): void;
  (e: "update:offset", value: number): void;
  (e: "update:limit", value: number): void;
  (
    e: "update:checked-rows",
    value: PerformanceEvaluationsQuery["performance_evaluations"]
  ): void;
}>();

const { multiSortMeta, orderBy } = usePrimeVueMultisort(props.orderBy);

watch(multiSortMeta, () => {
  emit("update:offset", 0);
  emit("update:order-by", orderBy.value);
});
</script>
